import React, { useState, useEffect } from "react";
import { ReligionTab } from "../types";
import { Target, Flame, Trophy, CheckCircle, Plus, RotateCcw, Award } from "lucide-react";
import { motion } from "motion/react";

interface RitualGoalsTrackerProps {
  activeReligion: ReligionTab;
}

const RELIGION_GOAL_CONFIGS: Record<ReligionTab, { defaultTargetMinutes: number; unitLabel: string; title: string }> = {
  hinduism: { defaultTargetMinutes: 20, unitLabel: "Vedic Chanting & Hawan", title: "Sanatana Dharma Streak" },
  islam: { defaultTargetMinutes: 15, unitLabel: "Salah & Tasbih Dhikr", title: "Islamic Daily Devotion" },
  buddhism: { defaultTargetMinutes: 15, unitLabel: "Anapanasati & Singing Bowl", title: "Buddhist Mindfulness Streak" },
  christianity: { defaultTargetMinutes: 15, unitLabel: "Holy Rosary & Hours", title: "Christian Liturgical Goal" },
  judaism: { defaultTargetMinutes: 15, unitLabel: "Tefillah & Shabbat Zmanim", title: "Judaism Practice Goal" },
  sikhism: { defaultTargetMinutes: 15, unitLabel: "Naam Simran & Gurbani", title: "Sikhi Devotional Goal" },
  taoism: { defaultTargetMinutes: 15, unitLabel: "Wu Wei & Microcosmic Orbit", title: "Taoist Cultivation Goal" },
  jainism: { defaultTargetMinutes: 15, unitLabel: "Navkar Japa & Ahimsa Vow", title: "Jain Spiritual Goal" },
  bahai: { defaultTargetMinutes: 15, unitLabel: "Hidden Words & 95 Alla-u-Abha", title: "Bahá'í Unity Goal" },
  zoroastrianism: { defaultTargetMinutes: 15, unitLabel: "Avestan Asha & Humata", title: "Zoroastrian Asha Goal" },
  shinto: { defaultTargetMinutes: 15, unitLabel: "Misogi Bow & Kami Gratitude", title: "Shinto Misogi Goal" },
  indigenous_americas: { defaultTargetMinutes: 15, unitLabel: "Four Directions & Smudging", title: "Indigenous Sacred Wheel Goal" },
  shamanism: { defaultTargetMinutes: 15, unitLabel: "120 BPM Drum & Pachamama", title: "Shamanic Heartbeat Goal" },
  universal: { defaultTargetMinutes: 15, unitLabel: "Solfeggio & Meditation", title: "Universal Sacred Goal" }
};

export default function RitualGoalsTracker({ activeReligion }: RitualGoalsTrackerProps) {
  const config = RELIGION_GOAL_CONFIGS[activeReligion] || RELIGION_GOAL_CONFIGS.universal;

  // Streak Target (e.g. 7 days, 21 days, 40 days, 108 days)
  const [targetStreakDays, setTargetStreakDays] = useState<number>(() => {
    try {
      const saved = localStorage.getItem(`ritual_goal_target_${activeReligion}`);
      return saved ? parseInt(saved, 10) : 21;
    } catch (e) {
      return 21;
    }
  });

  // Current Streak Count
  const [currentStreakDays, setCurrentStreakDays] = useState<number>(() => {
    try {
      const saved = localStorage.getItem(`ritual_goal_current_${activeReligion}`);
      return saved ? parseInt(saved, 10) : 3;
    } catch (e) {
      return 3;
    }
  });

  // Has completed today's practice
  const [completedToday, setCompletedToday] = useState<boolean>(() => {
    try {
      const today = new Date().toDateString();
      const lastCompleted = localStorage.getItem(`ritual_goal_last_date_${activeReligion}`);
      return lastCompleted === today;
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    try {
      const savedTarget = localStorage.getItem(`ritual_goal_target_${activeReligion}`);
      if (savedTarget) setTargetStreakDays(parseInt(savedTarget, 10));

      const savedCurrent = localStorage.getItem(`ritual_goal_current_${activeReligion}`);
      if (savedCurrent) setCurrentStreakDays(parseInt(savedCurrent, 10));

      const today = new Date().toDateString();
      const lastCompleted = localStorage.getItem(`ritual_goal_last_date_${activeReligion}`);
      setCompletedToday(lastCompleted === today);
    } catch (e) {}
  }, [activeReligion]);

  const handleCompleteToday = () => {
    const today = new Date().toDateString();
    if (!completedToday) {
      const newStreak = currentStreakDays + 1;
      setCurrentStreakDays(newStreak);
      setCompletedToday(true);

      try {
        localStorage.setItem(`ritual_goal_current_${activeReligion}`, newStreak.toString());
        localStorage.setItem(`ritual_goal_last_date_${activeReligion}`, today);
      } catch (e) {}
    } else {
      // Undo today
      const newStreak = Math.max(0, currentStreakDays - 1);
      setCurrentStreakDays(newStreak);
      setCompletedToday(false);

      try {
        localStorage.setItem(`ritual_goal_current_${activeReligion}`, newStreak.toString());
        localStorage.removeItem(`ritual_goal_last_date_${activeReligion}`);
      } catch (e) {}
    }
  };

  const handleTargetChange = (newTarget: number) => {
    setTargetStreakDays(newTarget);
    try {
      localStorage.setItem(`ritual_goal_target_${activeReligion}`, newTarget.toString());
    } catch (e) {}
  };

  const progressPercent = Math.min(100, Math.round((currentStreakDays / targetStreakDays) * 100));

  return (
    <div className="rounded-2xl bg-gradient-to-br from-stone-900 via-stone-900/90 to-amber-950/30 border border-amber-500/20 p-5 shadow-lg backdrop-blur-md space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-stone-800 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400">
            <Target className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-stone-100 tracking-wide">{config.title} Target</h3>
            <p className="text-xs text-stone-400">
              Track daily consistency & streak milestones for {config.unitLabel}.
            </p>
          </div>
        </div>

        {/* Target Streak Selector Buttons */}
        <div className="flex items-center gap-1.5 bg-stone-950 border border-stone-800 rounded-xl p-1">
          <span className="text-[10px] text-stone-400 font-medium px-2">Goal:</span>
          {[7, 21, 40, 108].map((days) => (
            <button
              key={days}
              onClick={() => handleTargetChange(days)}
              className={`px-2.5 py-1 rounded-lg text-xs font-mono font-bold transition-all ${
                targetStreakDays === days
                  ? "bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-sm shadow-amber-500/10"
                  : "text-stone-400 hover:text-stone-200"
              }`}
            >
              {days}d
            </button>
          ))}
        </div>
      </div>

      {/* Progress Stats & Bar */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-500 fill-orange-500/20 animate-pulse" />
            <span className="text-2xl font-black font-mono text-amber-200">{currentStreakDays}</span>
            <span className="text-xs text-stone-400 font-medium">/ {targetStreakDays} Days Streak</span>
          </div>

          <div className="flex items-center gap-1.5">
            <Trophy className="w-4 h-4 text-amber-400" />
            <span className="text-xs font-mono font-bold text-amber-300">{progressPercent}%</span>
          </div>
        </div>

        {/* Animated Progress Bar */}
        <div className="relative w-full h-3.5 bg-stone-950 rounded-full border border-stone-800 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="h-full bg-gradient-to-r from-amber-500 via-orange-500 to-amber-400 rounded-full shadow-md shadow-amber-500/20"
          />
        </div>

        {/* Action Button */}
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-stone-400 italic">
            {completedToday
              ? "✨ Today's sacred practice logged!"
              : "Mark today's practice complete to extend your streak."}
          </span>

          <button
            onClick={handleCompleteToday}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all border shadow-md ${
              completedToday
                ? "bg-emerald-950/80 border-emerald-500/50 text-emerald-300 hover:bg-emerald-900/60"
                : "bg-gradient-to-r from-amber-600 to-orange-600 border-amber-400/50 text-white hover:scale-105 active:scale-95"
            }`}
          >
            <CheckCircle className={`w-4 h-4 ${completedToday ? "text-emerald-400 fill-emerald-400/20" : ""}`} />
            <span>{completedToday ? "Completed Today" : "Log Today's Practice"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
